import { FormikErrors, FormikTouched } from "formik";
import { ChangeEvent, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CountryData } from "react-phone-input-2";
import {
  Formik,
  FormikInsurance,
  FormikContractorLegal,
  InputSelectOption,
} from "src/types";
import {
  useCompanyTypeOptions,
  useMunicipalitiesOptions,
  useProvincesOptions,
  useCapOptions,
} from "../../../../handlers/form-label-mapping.hook";
import { defaultSelectInputOptionValue } from "src/storage";

export function useLegalContractor({
  formik,
}: {
  formik: Formik<FormikInsurance>;
}) {
  const { t } = useTranslation();

  const legalContractor = formik.values.contractor as FormikContractorLegal;
  const legalTouched =
    (formik.touched.contractor as FormikTouched<FormikContractorLegal>) || {};
  const legalErrors =
    (formik.errors.contractor as FormikErrors<FormikContractorLegal>) || {};

  const { companyTypeOptions } = useCompanyTypeOptions();

  const { setFieldValue, setFieldError, setFieldTouched } = formik;

  const onChangePhone = (
    value: string,
    data: CountryData,
    event: ChangeEvent<HTMLInputElement>,
    formattedValue: string
  ) => {
    setFieldValue("contractor.companyPhone", {
      country: data,
      formattedPhone:
        value === (data as CountryData)?.dialCode ? value : formattedValue,
      value: formattedValue,
    });
  };

  // Nationality - CAP  =============================================
  // Handles logic when the user switches the contractor nationality.
  // Reset error and touched object
  const onSelectICompanyFCNotMatchVAT = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const companyFCNotMatchVAT = event.target.value;
    if (!companyFCNotMatchVAT) return;

    setFieldError("contractor.companyVAT", "");
    setFieldTouched("contractor.companyVAT", false, false);
    setFieldValue("contractor.companyVAT", "");
  };

  // Viario  =============================================
  const { provincesOptions } = useProvincesOptions();
  const { municipalitiesOptions: legalMunicipalitiesOptions } =
    useMunicipalitiesOptions(legalContractor.companyLegalProvince.id);

  const { capOptions: legalCapOptions } = useCapOptions(
    legalContractor.companyLegalProvince.id,
    legalContractor.companyLegalCity.id
  );

  const onSelectLegalProvince = (
    event: ChangeEvent<{ name: string; value: InputSelectOption }>
  ) => {
    setFieldTouched("contractor.companyLegalCity.value", true, true);
    setFieldValue(
      "contractor.companyLegalCity",
      { ...defaultSelectInputOptionValue },
      true
    );
    setFieldTouched("contractor.companyLegalCap.value", true, true);
    setFieldValue(
      "contractor.companyLegalCap",
      { ...defaultSelectInputOptionValue },
      true
    );
    let province = event.target.value;

    if (!province?.id || !province?.value)
      province = { ...defaultSelectInputOptionValue };

    setFieldValue("contractor.companyLegalProvince", province, false);
    setFieldTouched("contractor.companyLegalProvince.value", false, false);
  };

  const onSelectLegalMunicipality = (
    event: ChangeEvent<{ name: string; value: InputSelectOption }>
  ) => {
    setFieldTouched("contractor.companyLegalCap.value", true, true);
    setFieldValue(
      "contractor.companyLegalCap",
      { ...defaultSelectInputOptionValue },
      true
    );
    let municipality = event.target.value;

    if (!municipality?.id || !municipality?.value)
      municipality = { ...defaultSelectInputOptionValue };

    setFieldValue("contractor.companyLegalCity", municipality, false);
    setFieldTouched("contractor.companyLegalCity.value", false, false);
  };

  const onSelectLegalCap = (
    event: ChangeEvent<{ name: string; value: InputSelectOption }>
  ) => {
    let cap = event.target.value;

    if (!cap?.id || !cap?.value) cap = { ...defaultSelectInputOptionValue };

    setFieldValue("contractor.companyLegalCap", cap, false);
    setFieldTouched("contractor.companyLegalCap.value", true, false);
  };

  useEffect(() => {
    if (legalCapOptions.length !== 1) return;

    const cap = legalCapOptions[0];

    setFieldValue("contractor.companyLegalCap", cap, false);
    setFieldTouched("contractor.companyLegalCap.value", false, false);
  }, [legalCapOptions]);

  return {
    legalContractor,
    legalTouched,
    legalErrors,
    companyTypeOptions,
    provincesOptions,
    legalMunicipalitiesOptions,
    legalCapOptions,
    t,
    onChangePhone,
    onSelectICompanyFCNotMatchVAT,
    onSelectLegalProvince,
    onSelectLegalMunicipality,
    onSelectLegalCap,
  };
}
